import React, { useMemo } from 'react'
import { IUser } from '@/api/user/getUsers.ts'
import { calculateAge } from '@/util/person.ts'
import TruncatedText from '@/component/tooltip/TruncatedText.tsx'
import AlertStatusBadge from '@/component/badge/AlertStatusBadge.tsx'
import BatteryStatusBadge from '@/component/badge/BatteryStatusBadge.tsx'
import { DeviceStatus } from '@/api/user/getUsers.ts'
import useQueryGetUserRecentState from '@/api/user/getUserRecentState.ts'
import { useTranslation } from 'react-i18next'
import useQueryGetMe from '@/api/auth/getMe.ts'

export interface IProps extends IUser {
    className?: string
    onClick: (userId: number) => void
} 

const SearchUserItem: React.FC<IProps> = ({
    className,
    id,
    alertStatus,
    name,
    birthDate,
    gender,
    device,
    onClick
}) => {
    const { t } = useTranslation()
    const { data: me } = useQueryGetMe()
    
    // 실시간 상태 정보 가져오기
    const { data: recentState } = useQueryGetUserRecentState(id, 600)

    // 특정 이메일 계정인지 확인
    const isMaskingRequired = useMemo(() => {
        return me?.email === 'dbd_cm@clomental.com'
    }, [me?.email])

    // 마스킹 처리 함수
    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }

    const genderName = useMemo(() => {
        if (gender == 0) return t('users.list.item.gender.male')
        if (gender == 1) return t('users.list.item.gender.female')
        else return t('users.list.item.gender.none')
    }, [gender, t])

    // 실시간 배터리 정보 우선 사용
    const currentBattery = useMemo(() => {
        // 실시간 데이터가 있으면 우선 사용
        if (recentState?.summary?.battery !== undefined && recentState?.summary?.battery !== null) {
            return recentState.summary.battery
        }
        // 없으면 기본 device.battery 사용 (없으면 undefined)
        return device?.battery
    }, [recentState?.summary?.battery, device?.battery])

    // 실제 디바이스 상태 결정 (API의 device.status를 우선 사용)
    const actualDeviceStatus = useMemo(() => {
        if (!device) return undefined
        
        // 1. EMERGENCY 상태는 최우선 (어떤 경우에도 유지)
        if (device.status === DeviceStatus.EMERGENCY) {
            return DeviceStatus.EMERGENCY
        }
        
        // 2. 실시간 충전 상태 확인
        if (recentState?.summary?.isCharging) {
            return DeviceStatus.CHARGING
        }
        
        // 3. device.status가 있으면 그것을 사용
        if (device.status) {
            return device.status
        }
        
        // 4. 실시간 온라인 상태 확인 (device.status가 없을 때만)
        if (recentState?.summary?.isOnline !== undefined) {
            return recentState.summary.isOnline ? DeviceStatus.ONLINE : DeviceStatus.OFFLINE
        }
        
        return undefined
    }, [device, recentState?.summary?.isCharging, recentState?.summary?.isOnline])

    // 디바이스 상태에 따른 배경색 결정
    const getItemBackgroundColor = useMemo(() => {
        if (!device) return 'bg-sub_b' // 기본색
        
        switch (actualDeviceStatus) {
            case DeviceStatus.EMERGENCY:
                return 'bg-red-50 border-red-200'
            case DeviceStatus.CHARGING:
                return 'bg-green-50 border-green-200'
            case DeviceStatus.OFFLINE:
                return 'bg-gray-100 border-gray-300'
            case DeviceStatus.ONLINE:
                // 배터리 레벨에 따른 색상 (배터리 정보가 있을 때만)
                if (currentBattery !== undefined && currentBattery < 15) {
                    return 'bg-orange-50 border-orange-200'
                }
                return 'bg-sub_b border-gray-200'
            default:
                return 'bg-sub_b border-gray-200'
        }
    }, [device, actualDeviceStatus, currentBattery])

    return (
        <>
            <div
                className={`${className} ${getItemBackgroundColor} flex flex-col gap-[8px] px-[20px] py-[12px] overflow-hidden rounded-[10px] border hover:shadow-md hover:cursor-pointer active:shadow-lg transition-all duration-200`}
                onClick={() => onClick(id)}>
                
                {/* 상단: 알림 상태와 이름 */}
                <div className={'flex gap-[10px] justify-between items-center'}>
                <AlertStatusBadge value={alertStatus} className={'w-[15px] h-[15px]'} />
                    <TruncatedText className={'flex-1 text-center font-medium'} text={getMaskedName(name)} />
                </div>
                
                {/* 하단: 나이/성별과 배터리 상태 */}
                <div className={'flex justify-between items-center'}>
                    <p className={'text-sm text-gray-600'}>
                    {t('users.list.item.age', { count: calculateAge(birthDate) })} / {genderName}
                </p>
                    
                    {device ? (
                        <BatteryStatusBadge
                            battery={currentBattery}
                            status={actualDeviceStatus}
                            showPercentage={true}
                            className={'text-xs'}
                        />
                    ) : (
                        <div className={'px-2 py-1 rounded-md bg-gray-200 border border-gray-300'}>
                            <span className={'text-xs text-gray-600 font-medium'}>{t('users.list.item.device.disconnected')}</span>
                        </div>
                    )}
                </div>

                {/* 추가 상태 정보 */}
                {device && (
                    <div className={'text-xs text-gray-500 text-center'}>
                        {actualDeviceStatus === DeviceStatus.EMERGENCY && t('users.list.item.device.status.emergency')}
                        {actualDeviceStatus === DeviceStatus.CHARGING && t('users.list.item.device.status.charging')}
                        {actualDeviceStatus === DeviceStatus.OFFLINE && t('users.list.item.device.status.offline')}
                        {actualDeviceStatus === DeviceStatus.ONLINE && currentBattery < 15 && t('users.list.item.device.status.lowBattery')}
                        {actualDeviceStatus === DeviceStatus.ONLINE && currentBattery >= 15 && t('users.list.item.device.status.normal')}
                    </div>
                )}
            </div>
        </>
    )
}

export default SearchUserItem
